import React, { useState, useRef, useEffect } from 'react';
import { Columns3, Check, GripVertical } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

const ColumnSelector = ({ columns, visibleColumns, onToggleColumn }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (columnKey) => {
    onToggleColumn(columnKey);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        title={t('column_selector.columns')}
        className={`h-8 px-2.5 flex items-center gap-1.5 rounded-md border text-xs transition-colors ${isOpen ? 'text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/5' : 'text-muted-foreground border-border hover:text-foreground'}`}
      >
        <Columns3 className="w-3.5 h-3.5" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-lg shadow-xl z-50">
          <div className="p-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wider px-2 py-1.5 mb-1">
              {t('column_selector.show_hide_columns')}
            </div>
            {columns.filter(column => !column.required).map((column) => {
              const isVisible = visibleColumns[column.key] !== false;
              const isDisabled = column.required;

              return (
                <button
                  key={column.key}
                  onClick={() => !isDisabled && handleToggle(column.key)}
                  disabled={isDisabled}
                  className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg transition-colors ${
                    isDisabled
                      ? 'text-muted-foreground/70 cursor-not-allowed'
                      : 'text-foreground/80 hover:bg-accent/60 hover:text-foreground'
                  }`}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                    isVisible
                      ? 'bg-amber-500 border-amber-500'
                      : 'border-input'
                  }`}>
                    {isVisible && <Check className="w-3 h-3 text-black" />}
                  </div>
                  <span className="text-sm">{column.label}</span>
                  {isDisabled && (
                    <span className="ml-auto text-xs text-muted-foreground/70">({t('common.required').toLowerCase()})</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnSelector;
